// Betonijerry FCM client init — port of puminet4/src/firebase/firebase.js.
// Shared betonionline Firebase project; service worker registered from /public.
// Token issuance is gated behind a user gesture in SubscribePushButton — do NOT
// call requestPermission() at module load or on page open.

import { initializeApp } from "firebase/app";
import { getMessaging, getToken, isSupported } from "firebase/messaging";
import { captureError } from "src/utils/errorReporting";

const FCM_TOKEN_TIMEOUT_MS = 10000;

const firebaseApp = initializeApp({
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
});

let _messagingPromise = null;

/**
 * Returns the Messaging instance, or `null` if the browser cannot do FCM (no
 * support, older iOS Safari outside a PWA, etc). Memoised — `isSupported()` is
 * only called once per page load.
 */
export async function getMessagingInstance() {
  if (_messagingPromise) return _messagingPromise;
  _messagingPromise = (async () => {
    try {
      return (await isSupported()) ? getMessaging(firebaseApp) : null;
    } catch {
      return null;
    }
  })();
  return _messagingPromise;
}

function withTimeout(promise, ms, op) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error(`${op} timed out after ${ms}ms`)), ms)),
  ]);
}

/**
 * Request notification permission and fetch an FCM token. Must be called from a
 * user gesture (button click) — browsers reject permission prompts triggered on load.
 * Returns { success, token, error? }. Caller is responsible for POSTing the token
 * to the backend (see src/utils/notifications/notificationApi.js).
 */
export async function requestPermission() {
  try {
    if (!("serviceWorker" in navigator)) {
      return { success: false, error: "SERVICE_WORKER_FAILED", token: null };
    }
    try {
      await navigator.serviceWorker.register("/firebase-messaging-sw.js");
    } catch (error) {
      captureError(error, { tags: { feature: "firebase", operation: "registerServiceWorker" } });
      return { success: false, error: "SERVICE_WORKER_FAILED", token: null };
    }

    const permission = await Notification.requestPermission();
    if (permission !== "granted") return { success: false, error: "PERMISSION_DENIED", token: null };

    const msg = await getMessagingInstance();
    if (!msg) return { success: false, error: "TOKEN_RETRIEVAL_FAILED", token: null };

    let token = null;
    try {
      token = await withTimeout(
        getToken(msg, { vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY }),
        FCM_TOKEN_TIMEOUT_MS,
        "FCM token retrieval"
      );
    } catch (error) {
      captureError(error, { tags: { feature: "firebase", operation: "getFCMToken" } });
    }
    if (!token) return { success: false, error: "TOKEN_RETRIEVAL_FAILED", token: null };

    return { success: true, token };
  } catch (error) {
    captureError(error, { tags: { feature: "firebase", operation: "requestPermission" } });
    return { success: false, error: error.message, token: null };
  }
}
